/**
 * useShellWaitingRooms - staged combatants nobody else can see, and the ways in and out.
 *
 * - `canSeeRooms` stops a player asking a question the rules already
 *   answer; it is a courtesy, not the guard (that is `firestore.rules`).
 * - `narrateRoom(text, tabId = activeTab)`: ONE narration helper for the
 *   room verbs. `tabId` names whose log the line goes to - a replace
 *   addresses the newcomer, since the departed is in a room nobody watches
 *   (see gotchas: a room verb's log line lands in whoever was the target).
 * - ONE DOOR for admitting characters (`useCharacterAdmission`, v8.47.0).
 * - The room verbs (`useWaitingRoomActions`, v8.48.0), wired to the turn
 *   order: a recall leaves the order unless the GM rules a room is a wing,
 *   disposal is final either way, and a replace (v8.343.0) re-aims the
 *   panels at the stand-in.
 * - The deploy bar's REPLACE dialog: the departed's own state can forbid it
 *   before anyone is chosen, so that sentence opens the dialog; a drop onto
 *   a marker is refused with a toast - it is the GM's to hear.
 *
 * Extracted from DeathWatchSimulator.
 */
import { useState, useCallback } from 'react';
import * as multiplayer from '../multiplayer';
import * as settings from '../engine/settings/resolve';
import { refuseReplace } from '../engine/roster/replace';
import useWaitingRooms from './useWaitingRooms';
import useCharacterAdmission from './useCharacterAdmission';
import useWaitingRoomActions from './useWaitingRoomActions';
import { infoLine } from './shellCombatant';

/** Why the picked marker may not be replaced at all, or null. */
export function earlyRefusal(outgoing, { roster, tokens, isDefencePending }) {
  if (!outgoing) return null;
  return refuseReplace({
    pendingIds: new Set(roster.filter((c) => isDefencePending(c.id)).map((c) => c.id)),
    outgoingId: outgoing.id,
    incomingId: '',
    roster,
    tokens,
  });
}

/** The turn-order hooks the room verbs are handed. */
function turnOrderHooks(turnTracker, permissions) {
  const remove = (combatantId) => turnTracker.removeParticipant('tab-' + combatantId);
  return {
    admitToTurnOrder: turnTracker.trackerState.active ? turnTracker.addTabParticipantBatch : null,
    removeFromTurnOrder: settings.resolveRecallKeepsTurn(permissions) ? null : remove,
    forceRemoveFromTurnOrder: remove,
    replaceInTurnOrder: turnTracker.replaceParticipant,
  };
}

/** The room verbs, with the turn order and the pointers wired in. */
function useRoomVerbs(args, waiting, narrateRoom, { admit, record }) {
  const { activeTab, actorId, setActiveTab, setActorId } = args;
  return useWaitingRoomActions({
    ...turnOrderHooks(args.turnTracker, args.permissions),
    waiting,
    roster: args.roster,
    setRoster: args.setRoster,
    removeToken: args.removeToken,
    removeCombatant: args.removeCombatant,
    pointAtIfUnset: args.pointAtIfUnset,
    narrate: narrateRoom,
    admit,
    record,
    tokens: args.tokens,
    writeToken: args.writeToken,
    isDefencePending: args.isDefencePending,
    repoint: (fromId, toId) => {
      if (activeTab === fromId) setActiveTab(toId);
      if (actorId === fromId) setActorId(toId);
    },
    // A hidden figure is refused at the room's door; the GM is told why (plan map-display, G1).
    isHidden: args.hidden?.isHidden,
    refuse: (text) => args.pushToast?.(text, { type: 'error', duration: 6000 }),
  });
}

/** The REPLACE dialog's state, its early refusal, and both ways to confirm. */
function useReplace(args, replaceWith) {
  const [replaceDialog, setReplaceDialog] = useState(null); // { outgoingId } | null (v8.344.0)
  const replaceOutgoing = replaceDialog ? args.roster.find((c) => c.id === replaceDialog.outgoingId) : null;
  const replaceFromDialog = useCallback(
    (source) => {
      const refusal = replaceWith(replaceDialog.outgoingId, source);
      if (!refusal) setReplaceDialog(null);
      return refusal;
    },
    [replaceWith, replaceDialog],
  );
  const { pushToast } = args;
  const replaceFromDrop = useCallback(
    (memberId, outgoingId) => {
      const refusal = replaceWith(outgoingId, { memberId });
      if (refusal) pushToast(refusal, { type: 'error' });
    },
    [replaceWith, pushToast],
  );
  const replaceEarlyRefusal = earlyRefusal(replaceOutgoing, args);
  return { setReplaceDialog, replaceOutgoing, replaceEarlyRefusal, replaceFromDialog, replaceFromDrop };
}

/**
 * @param {object} args - sessionId, isLocked, activeTab, actorId,
 *   setActiveTab, setActorId, roster, setRoster, tokens, writeToken,
 *   removeToken, removeCombatant, pointAtIfUnset, isDefencePending,
 *   turnTracker, permissions, activeLibrary, addNarrativeRef, pushToast.
 */
export default function useShellWaitingRooms(args) {
  const { activeTab, addNarrativeRef } = args;
  const canSeeRooms = !args.isLocked('waitingRoom');
  const waiting = useWaitingRooms({ sessionId: args.sessionId, mp: multiplayer, canSee: canSeeRooms });
  const narrateRoom = useCallback(
    // Who came, who went, where the scene cut to: the story's lane in the log (v9.7.0, engine/log/lanes).
    (text, tabId = activeTab) => addNarrativeRef.current?.(tabId, { ...infoLine(text), lane: 'story' }),
    [activeTab, addNarrativeRef],
  );
  const admission = useCharacterAdmission({ roster: args.roster, rooms: waiting.rooms, library: args.activeLibrary });
  const verbs = useRoomVerbs(args, waiting, narrateRoom, admission);
  const replace = useReplace(args, verbs.replaceWith);
  return { canSeeRooms, waiting, narrateRoom, ...admission, ...verbs, ...replace };
}
